import { useEffect, useRef } from 'react';
import { useLocation, matchPath, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usePermissions } from '@/context/PermissionContext';
import { useAuth } from '@/context/AuthContext';
import type { PermissionScope } from '@/types/permissions';
import { getRouteMap } from '@/utility';
import { mainLayoutRoutes } from '@/routes/config';
import { useLoader } from '@/context/LoaderContext';

const PERMISSION_PRIORITY = [
  'dashboard.read',
  'employee.read',
  'department.read',
  'designation.read',
  'asset.read',
  'access.read',
  'company_role.read',
  'company.read',
] as const;

const PERMISSION_ROUTE_MAP: Record<string, string> = {
  'dashboard.read': '/dashboard',
  'employee.read': '/employees',
  'department.read': '/department',
  'designation.read': '/designation',
  'asset.read': '/assets',
  'access.read': '/user-access',
  'company_role.read': '/roles',
  'company.read': '/company-info',
};

const routeMap = getRouteMap(mainLayoutRoutes);

/**
 * Converts permission string into human-readable message
 */
const permissionErrorMessage = (permission: string) => {
  const [, action] = permission.split('.');
  return `You don’t have ${action} permission for this page`;
};

/**
 * Global permission handler
 *
 * Responsibilities:
 * 1. Listen to backend permission-denied events
 * 2. Show toast with missing permission
 * 3. If READ permission is missing/revoked → redirect by hierarchy
 */
export function usePermissionHandler() {
  const { permissions, fetchingPermissions } = usePermissions();
  const { companyId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { startNavigationLoading, stopNavigationLoading } = useLoader();
  const lastPermissionRef = useRef<string | null>(null);
  const pendingPathRef = useRef<string | null>(null);
  // Tracks the companyId that triggered the in-progress permissions fetch.
  // undefined between fetches — prevents the redirect logic from re-running
  // on unrelated re-renders (permission set updates, pathname changes, etc).
  const switchedCompanyIdRef = useRef<string | null | undefined>(undefined);

  // Stop the loader once the pathname matches the pending redirect target
  useEffect(() => {
    if (pendingPathRef.current && location.pathname === pendingPathRef.current) {
      pendingPathRef.current = null;
      stopNavigationLoading();
    }
    return () => {
      stopNavigationLoading();
    }
  }, [location.pathname, stopNavigationLoading]);

  useEffect(() => {
    const onPermissionDenied = (event: Event) => {
      const rawPermissions = (event as CustomEvent)?.detail?.permission;
      if (!rawPermissions) return;

      const denied = Array.isArray(rawPermissions)
        ? rawPermissions
        : [rawPermissions];
      const permission = denied.slice().sort().join('|');

      if (lastPermissionRef.current === permission) return;
      lastPermissionRef.current = permission;

      toast.dismiss();
      toast.error(permissionErrorMessage(denied[0]));

      const deniedRead = denied.find((p) => p.endsWith('.read'));
      if (!deniedRead) return;

      const deniedIndex = PERMISSION_PRIORITY.indexOf(deniedRead);

      const redirectPermission =
        PERMISSION_PRIORITY.slice(deniedIndex + 1).find((p) =>
          permissions.has(p)
        ) ?? PERMISSION_PRIORITY.find((p) => permissions.has(p));

      const target = redirectPermission
        ? PERMISSION_ROUTE_MAP[redirectPermission]
        : '/profile';
      pendingPathRef.current = target;
      startNavigationLoading();
      navigate(target, { replace: true });
    };

    window.addEventListener('permission-denied', onPermissionDenied);

    return () => {
      window.removeEventListener('permission-denied', onPermissionDenied);
    };
  }, [permissions, navigate]);

  // After a company switch, permissions refetch under a new query key.
  // Once they settle, check if the current route is accessible and redirect if not.
  useEffect(() => {
    if (fetchingPermissions) {
      // Record which companyId triggered this fetch
      switchedCompanyIdRef.current = companyId;
      return;
    }

    // undefined = no fetch has started yet, skip (initial mount)
    if (switchedCompanyIdRef.current === undefined) return;

    // companyId changed again while fetching (rapid switch) — stale result, skip
    if (switchedCompanyIdRef.current !== companyId) return;

    // Reset so subsequent renders don't re-trigger
    switchedCompanyIdRef.current = undefined;

    const pathname = location.pathname;
    const entry =
      routeMap.get(pathname) ??
      [...routeMap.entries()].find(([pattern]) =>
        matchPath(pattern, pathname)
      )?.[1];

    if (!entry?.scope) return;

    const scopes = Array.isArray(entry.scope) ? entry.scope : [entry.scope];
    const hasAccess = scopes.some((s) => permissions.has(s as PermissionScope));

    if (!hasAccess) {
      const fallbackPermission = PERMISSION_PRIORITY.find((p) =>
        permissions.has(p)
      );
      const target = fallbackPermission
        ? PERMISSION_ROUTE_MAP[fallbackPermission]
        : '/profile';
      pendingPathRef.current = target;
      startNavigationLoading();
      navigate(target, { replace: true });
    }
  }, [fetchingPermissions, permissions, companyId, location.pathname, navigate, startNavigationLoading]);
}
